'use client';

import { useEffect } from 'react';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { getIsIosSafari } from 'src/utils/browser';

/**
 * Code from here: https://medium.com/@kristiantolleshaugmrch/fixing-the-double-tap-issue-in-ios-safari-with-javascript-4e72a18a1feb
 *
 * This is a workaround to fix the double tap issue in iOS Safari.
 *
 * The issue is that when you tap on a button, the first tap may trigger a hover event only.
 * It is only on the second tap that the click event is triggered.
 *
 * This code prevents the double tap issue by preventing the default behavior of the click event.
 *
 */

const IOSSafariHoverFix = () => {
	const isMobileScreenSize = useIsMobileScreenSize();

	useEffect(() => {
		const removeHoverIosSafari = () => {
			if (!isMobileScreenSize || !getIsIosSafari()) {
				return;
			}

			// Tags of interest: Only process certain interactive elements
			const shouldPrevent = (target: HTMLElement): boolean => {
				const tagName = target.tagName.toLowerCase();
				const datasetBind = target.dataset.bind;
				const preventFilter =
					(datasetBind && datasetBind.indexOf('click') > -1) ||
					tagName === 'a' ||
					tagName === 'div' ||
					tagName === 'input' ||
					tagName === 'span' ||
					tagName === 'img' ||
					tagName === 'path' ||
					tagName === 'svg' ||
					tagName === 'button';
				return preventFilter;
			};

			const eventSelector = {
				touchend: (_: Event, target: HTMLElement) => {
					// Reset any flags on touchend.
					target.dataset._clicked_ = '';
					target.dataset._mousemove_ = '0';
					target.dataset._timeOutId_ = '';
				},
				mouseover: (e: Event) => {
					// Prevent default hover behavior.
					e.preventDefault();
				},
				mousemove: (e: Event, target: HTMLElement) => {
					// Prevent default hover behavior.
					e.preventDefault();

					let _mousemoves = +(target.dataset._mousemove_ || '0');
					_mousemoves = _mousemoves + 1;
					target.dataset._mousemove_ = _mousemoves.toString();

					if (_mousemoves > 1 && !target.dataset._timeOutId_) {
						const id = setTimeout(
							() => {
								// Simulate a click after mouse move.
								target.click();
							},
							80 // Adjust delay here to fine-tune the click event timing.
						);
						target.dataset._timeOutId_ = id.toString();
					}
				},
				click: (e: Event, target: HTMLElement) => {
					// Prevent double click
					if (target.dataset._clicked_) {
						// This check prevents interference with valid tracking
						// and programmatic click events.
						if (e.isTrusted) {
							e.preventDefault();
						}

						return;
					}

					// Prevent timeout click
					if (target.dataset._timeOutId_) {
						clearTimeout(+target.dataset._timeOutId_);
					}

					// Mark element as clicked
					target.dataset._clicked_ = 'true';
				},
			};

			const preventHover = (e: Event) => {
				const target = e.target as HTMLElement | null;
				// Skip elements that don't have click handlers or necessary attributes
				if (
					!(target && (target as any).click && target.tagName && target.dataset)
				) {
					return;
				}

				if (!shouldPrevent(target)) {
					return;
				}

				const type = e.type as keyof typeof eventSelector;
				if (eventSelector[type]) {
					eventSelector[type](e, target);
				}
			};

			// Add event listeners for touch and mouse events
			document.addEventListener('touchend', preventHover, true);
			document.addEventListener('mouseover', preventHover, true);
			document.addEventListener('mousemove', preventHover, true);
			document.addEventListener('click', preventHover, true);

			// Return cleanup function
			return () => {
				document.removeEventListener('touchend', preventHover, true);
				document.removeEventListener('mouseover', preventHover, true);
				document.removeEventListener('mousemove', preventHover, true);
				document.removeEventListener('click', preventHover, true);
			};
		};

		const cleanup = removeHoverIosSafari();

		return cleanup;
	}, [isMobileScreenSize]);

	return null;
};

export default IOSSafariHoverFix;
